import React, { useState, useEffect } from 'react';
import './BucketGenerator.css';

const BucketGenerator = () => {
  const [seed, setSeed] = useState(0);
  const [numBuckets, setNumBuckets] = useState(3);
  const [status, setStatus] = useState('');
  const [question, setQuestion] = useState('');
  const [meta, setMeta] = useState('');
  const [pathStates, setPathStates] = useState('');
  const [pathStatesWithActions, setPathStatesWithActions] = useState('');

  // -------- Deterministic RNG ---------
  function mulberry32(a) {
    return function() {
      let t = a += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function seededInt(rng, lo, hi) {
    return Math.floor(rng() * (hi - lo + 1)) + lo;
  }

  // -------- Utilities ---------
  const keyOf = (arr) => arr.join(',');
  const parseState = (s) => s.split(',').map(x => +x);
  function clone(a) { return a.slice(); }

  // Generate capacities and initial state
  function generateInstance(seed, n) {
    const rng = mulberry32(seed >>> 0);
    const caps = Array.from({ length: n }, _ => seededInt(rng, 2, 14)); // 2..14
    const fullIdx = seededInt(rng, 0, n - 1);
    const init = Array(n).fill(0);
    init[fullIdx] = caps[fullIdx];
    return { caps, init, fullIdx };
  }

  // BFS over pour actions (i -> j until one stops)
  function bfs(caps, init) {
    const q = [init];
    const seen = new Map();         // stateKey -> depth
    const parent = new Map();       // stateKey -> {prevKey, action}
    seen.set(keyOf(init), 0);
    let head = 0;
    const layers = [[keyOf(init)]];

    while (head < q.length) {
      const s = q[head++];
      const d = seen.get(keyOf(s));
      for (let i = 0; i < s.length; i++) {
        for (let j = 0; j < s.length; j++) {
          if (i === j) continue;
          if (s[i] === 0 || s[j] === caps[j]) continue;
          const next = clone(s);
          const can = Math.min(s[i], caps[j] - s[j]);
          next[i] -= can;
          next[j] += can;
          const k = keyOf(next);
          if (!seen.has(k)) {
            seen.set(k, d + 1);
            parent.set(k, { prev: keyOf(s), action: `pour ${i + 1}→${j + 1}` });
            q.push(next);
            if (!layers[d + 1]) layers[d + 1] = [];
            layers[d + 1].push(k);
          }
        }
      }
    }
    return { seen, parent, layers };
  }

  // Choose an interesting question target
  function chooseTarget(instance, bfsResult) {
    const { layers } = bfsResult;
    const firstDepth = new Map(); // vol -> depth
    const freqAtDepth = new Map(); // depth#vol -> count
    for (let d = 0; d < layers.length; d++) {
      for (const k of (layers[d] || [])) {
        const s = parseState(k);
        for (const v of s) {
          if (!firstDepth.has(v)) firstDepth.set(v, d);
          const key = d + ':' + v;
          freqAtDepth.set(key, (freqAtDepth.get(key) || 0) + 1);
        }
      }
    }
    let best = null;
    for (let d = layers.length - 1; d >= 0; d--) {
      for (const k of (layers[d] || [])) {
        const s = parseState(k);
        for (const v of s) {
          if (v === 0) continue;
          if (firstDepth.get(v) === d && (freqAtDepth.get(d + ':' + v) === 1)) {
            best = { type: 'volume', depth: d, vol: v, stateKey: k };
            break;
          }
        }
        if (best) break;
      }
      if (best) break;
    }
    if (best) return best;
    for (let d = layers.length - 1; d >= 0; d--) {
      if ((layers[d] || []).length > 0) {
        const k = layers[d][0];
        return { type: 'state', depth: d, stateKey: k };
      }
    }
    return { type: 'state', depth: 0, stateKey: keyOf(instance.init) };
  }

  // Reconstruct a shortest path to any state that satisfies target
  function reconstructPath(target, instance, bfsResult) {
    const { parent, layers } = bfsResult;
    if (target.type === 'state') {
      const path = [];
      let k = target.stateKey;
      while (k) {
        const p = parent.get(k);
        path.push(k);
        k = p ? p.prev : null;
      }
      return path.reverse();
    } else {
      const candidates = (layers[target.depth] || []).filter(k => parseState(k).includes(target.vol));
      const end = candidates[0];
      const path = [];
      let k = end;
      while (k) {
        const p = parent.get(k);
        path.push(k);
        k = p ? p.prev : null;
      }
      return path.reverse();
    }
  }

  function describeQuestion(instance, target, steps) {
    const { caps, init, fullIdx } = instance;
    const capStr = caps.map((c, i) => (i === fullIdx ? `${c}L (full)` : `${c}L`)).join(', ');
    const milk = init.reduce((a, b) => a + b, 0);
    if (target.type === 'volume') {
      return `Anna has ${caps.length} buckets with capacities ${capStr}. Initially, only the ${caps[fullIdx]}L bucket is full (${milk}L of milk total).
<strong>Task:</strong> Measure exactly <span class="pill">${target.vol}L</span> in a single bucket by pouring between buckets (no marks).`;
    } else {
      const s = parseState(target.stateKey);
      return `Anna has ${caps.length} buckets with capacities ${capStr}. Initially, only the ${caps[fullIdx]}L bucket is full (${milk}L total).
<strong>Task:</strong> Reach the exact configuration <span class="pill">(${s.join(', ')})</span> by pouring between buckets (no marks).`;
    }
  }

  // Quality heuristic for choosing "good" puzzles
  function isGoodPuzzle(target, steps) {
    return target.type === 'volume' && steps >= 3 && steps <= 20;
  }

  // Render one instance (no retries)
  async function render(seed, n) {
    const instance = generateInstance(seed, n);
    const bfsResult = bfs(instance.caps, instance.init);
    const target = chooseTarget(instance, bfsResult);
    const path = reconstructPath(target, instance, bfsResult);
    const steps = path.length - 1;

    // Update UI: question and meta
    setQuestion(describeQuestion(instance, target, steps));
    const milk = instance.init.reduce((a, b) => a + b, 0);
    setMeta(`Capacities: <span class="pill">${instance.caps.join(', ')}</span> · Initial: <span class="pill">${instance.init.join(', ')}</span> · Total: <span class="pill">${milk}L</span> · Min pours: <span class="pill">${steps}</span>`);

    // Build text-only path output
    const lines = [];
    for (let i = 0; i < path.length; i++) {
      const k = path[i];
      const s = parseState(k);
      lines.push(s.join(','));
    }
    setPathStates(lines.join('\n'));

    // For copy+paste with actions too
    const stepsWithActions = [];
    for (let i = 0; i < path.length; i++) {
      const k = path[i];
      const s = parseState(k);
      if (i === 0) {
        stepsWithActions.push(`${s.join(',')}`);
      } else {
        const parent = bfsResult.parent.get(k);
        stepsWithActions.push(`${s.join(',')}    (${parent?.action || ''})`);
      }
    }
    setPathStatesWithActions(stepsWithActions.join('\n'));

    return { instance, target, steps };
  }

  // Try up to 100 seeds (seed, seed+1, ..., seed+99) to find a "good" puzzle
  async function run(seedInput, n) {
    let actualSeed = seedInput;
    if (seedInput === 0) {
      actualSeed = Math.floor(Math.random() * 2 ** 31);
      setSeed(actualSeed);
    }
    n = Math.min(6, Math.max(3, n));
    setStatus(`Seed <span class="pill">${actualSeed}</span> · Buckets <span class="pill">${n}</span> · Trying up to <span class="pill">100</span> variants...`);

    let best = null;
    for (let t = 0; t < 100; t++) {
      const testSeed = (actualSeed + t) >>> 0;
      setStatus(`Testing seed <span class="pill">${testSeed}</span> (attempt ${t + 1}/100)...`);
      const r = await render(testSeed, n);
      if (isGoodPuzzle(r.target, r.steps)) {
        setStatus(`✅ Using seed <span class="pill">${testSeed}</span> (attempt ${t + 1}) — looks good.`);
        setSeed(testSeed);
        return;
      }
      if (!best || (r.target.type === 'volume' && r.steps > best.steps && r.steps <= 25)) {
        best = { seed: testSeed, steps: r.steps, targetType: r.target.type };
      }
      // Add small delay to allow UI to update
      if (t % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
    if (best) {
      setStatus(`⚠️ No perfect match found in 100 tries. Showing the best attempt (seed ${best.seed}, ${best.targetType}, steps=${best.steps}).`);
      setSeed(best.seed);
      await render(best.seed, n);
    } else {
      setStatus(`❌ Could not generate a puzzle. Try different inputs.`);
    }
  }

  const handleRun = () => {
    run(seed, numBuckets);
  };

  const handleNewSeed = () => {
    setSeed(0);
    run(0, numBuckets);
  };

  const handleCopyPath = async () => {
    try {
      await navigator.clipboard.writeText(pathStates || '');
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const handleCopySteps = async () => {
    try {
      await navigator.clipboard.writeText(pathStatesWithActions || '');
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  // Auto-run on first load
  useEffect(() => {
    run(0, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bucket-generator">
      <header>
        <h1>🪣 Bucket Puzzle Generator — Shortest Path</h1>
      </header>
      <div className="wrap">
        <div className="panel">
          <div className="row">
            <div>
              <label>Seed (0 = random; will try +1 up to +99 if needed)</label>
              <input
                type="number"
                value={seed}
                onChange={(e) => setSeed(Number(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleRun()}
              />
            </div>
            <div>
              <label>Number of buckets (3–6)</label>
              <input
                type="number"
                min="3"
                max="6"
                value={numBuckets}
                onChange={(e) => setNumBuckets(Number(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleRun()}
              />
            </div>
            <div className="row">
              <button onClick={handleRun}>Run</button>
              <button className="ghost" onClick={handleNewSeed}>New seed & Run</button>
            </div>
          </div>
          <div className="mt" dangerouslySetInnerHTML={{ __html: status }}></div>
        </div>

        <div className="panel mt">
          <div className="mono" dangerouslySetInnerHTML={{ __html: question }}></div>
          <div className="mono mt" dangerouslySetInnerHTML={{ __html: meta }}></div>
          <h3 className="mt" style={{ marginBottom: '6px' }}>Shortest path states (start → solution)</h3>
          <pre className="mono">{pathStates}</pre>
          <div className="row">
            <button className="ghost" onClick={handleCopyPath}>Copy states</button>
            <button className="ghost" onClick={handleCopySteps}>Copy states + actions</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BucketGenerator;
